// Display helpers — keep them in one place so locale/currency stay consistent.

export function formatCents(cents: number, currency = 'EUR', locale = 'lt-LT'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * Render row–shelf–position coordinates as `A-3-2`. Empty parts are dropped
 * so "row A, shelf 3, no position" comes out as `A-3`. Used everywhere
 * pallet or SKU location is displayed.
 */
export function formatLocationCoords(loc: {
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
}): string {
  const parts = [loc.locationRow, loc.locationShelf, loc.locationPosition].filter(
    (x): x is string => Boolean(x && x.length > 0),
  );
  return parts.length === 0 ? '—' : parts.join('-');
}

/** Render a stock quantity. Prisma sends `Decimal` as a string; coerce safely. */
export function formatQty(qty: string | number | null | undefined): string {
  if (qty === null || qty === undefined) return '—';
  const n = typeof qty === 'number' ? qty : Number(qty);
  if (!Number.isFinite(n)) return String(qty);
  // Trim trailing zeros so "5" doesn't render as "5.0000".
  return Number.isInteger(n) ? n.toString() : n.toString().replace(/\.?0+$/, '');
}

/** ISO date (YYYY-MM-DD) — matches what we store on date columns. */
export function formatDate(d: Date | string | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toISOString().slice(0, 10);
}

/**
 * Date + time as `YYYY-MM-DD HH:mm` in Europe/Vilnius. Times are stored UTC and
 * shown in local warehouse time (TRS). Used for order stage timestamps (WMS-27).
 */
export function formatDateTime(d: Date | string | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('lt-LT', {
    timeZone: 'Europe/Vilnius',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const p = (type: string) => parts.find((x) => x.type === type)?.value ?? '';
  return `${p('year')}-${p('month')}-${p('day')} ${p('hour')}:${p('minute')}`;
}

/** Difference in whole days between `target` and today (positive = future). */
export function daysFromToday(target: Date | string): number {
  const t = typeof target === 'string' ? new Date(target) : target;
  const today = new Date();
  const a = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const b = Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

export type PaidUntilTone = 'green' | 'amber' | 'red' | 'unknown';

/**
 * Paid-until chip color logic — TRS §5.5.
 *   Green : paid-until > today + threshold
 *   Amber : today + threshold ≥ paid-until > today
 *   Red   : paid-until ≤ today (overdue or today)
 */
export function paidUntilTone(
  paidUntil: Date | string | null | undefined,
  thresholdDays: number,
): PaidUntilTone {
  if (!paidUntil) return 'unknown';
  const delta = daysFromToday(paidUntil);
  if (delta <= 0) return 'red';
  if (delta <= thresholdDays) return 'amber';
  return 'green';
}
